import { ArrowRightOutlined } from "@ant-design/icons";
import "./quote-button.css";
import { useRef } from "react";

// Tracks last touch end across components to suppress synthetic clicks after navigation.
let lastTouchTimestamp = 0;

type QuoteButtonProps = {
  children: React.ReactNode;
  onClick?: (e?: any) => void;
  onDisabledClick?: () => void;
  disabled?: boolean;
  hideArrow?: boolean;
  style?: React.CSSProperties;
};

const QuoteButton = ({ children, onClick, onDisabledClick, disabled, hideArrow, style }: QuoteButtonProps) => {
  const touchStartedRef = useRef(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    touchStartedRef.current = true;
    e.stopPropagation();
  };

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStartedRef.current) return;
    touchStartedRef.current = false;
    lastTouchTimestamp = Date.now();
    if (disabled) {
      onDisabledClick && onDisabledClick();
    } else {
      onClick && onClick();
    }
  };

  // A touch that turns into a scroll (or that the browser otherwise cancels)
  // never reaches onTouchEnd, so without this the ref stayed true and the NEXT
  // touchend — possibly on a different gesture — fired the button.
  const handleTouchCancel = () => {
    touchStartedRef.current = false;
  };

  const handleClick = (e: React.MouseEvent) => {
    const msSinceTouch = Date.now() - lastTouchTimestamp;
    if (msSinceTouch >= 0 && msSinceTouch < 700) {
      return;
    }

    if (disabled) {
      onDisabledClick && onDisabledClick();
    } else {
      onClick && onClick();
    }
  };

  // Keyboard activation. The element stays a div (its touch handling is tuned
  // for the funnel's ghost-tap problem), so it has to carry the button role and
  // Enter/Space itself; both route through the same disabled/enabled branch as
  // a pointer click.
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key !== "Enter" && e.key !== " ") return;
    e.preventDefault();
    if (disabled) {
      onDisabledClick && onDisabledClick();
    } else {
      onClick && onClick();
    }
  };

  return (
    <div
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchCancel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-disabled={disabled}
      className={`quote-button-container ${disabled ? "disabled" : ""}`}
      style={style}
    >
      <span>{children}</span>
      {!hideArrow && (
        <span className="quote-button-arrow">
          <ArrowRightOutlined />
        </span>
      )}
    </div>
  );
};

export default QuoteButton;
